import { useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useOperatedAirports } from '@/lib/disruptions/use-disruptions';

// URL-driven flights-monitor filters (NEX-342 §2/§3). All state lives in the URL
// so links are shareable + reload-safe. The airport scope is a self-contained
// multi-select over the tenant's operated airports (the same picker the
// disruptions create dialog uses), written to `?airports=` as a CSV — the same
// contract so it later swaps to the shared topbar picker (NEX-310).

const STATUS_OPTIONS = ['', 'SCHEDULED', 'DELAYED', 'DEPARTED', 'ARRIVED', 'CANCELLED'];

/** Direction toggle — `network` keys, resolved at render time. */
const DIRECTIONS: { value: string; labelKey: string }[] = [
  { value: 'any', labelKey: 'filters.direction.any' },
  { value: 'origin', labelKey: 'filters.direction.origin' },
  { value: 'destination', labelKey: 'filters.direction.destination' },
];

export function FlightsFilterBar() {
  const { t } = useTranslation('network');
  const [params, setParams] = useSearchParams();
  const airports = useOperatedAirports();

  const update = (key: string, value: string): void => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const selectedAirports = new Set(
    (params.get('airports') ?? '')
      .split(',')
      .map((c) => c.trim().toUpperCase())
      .filter(Boolean),
  );

  const toggleAirport = (code: string): void => {
    const next = new Set(selectedAirports);
    if (next.has(code)) next.delete(code);
    else next.add(code);
    update('airports', [...next].join(','));
  };

  const direction = params.get('direction') ?? 'any';

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border border-border bg-muted/30 px-3 py-1.5 text-xs">
      {/* Self-contained airport scope (multi-select → ?airports= CSV). */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs uppercase text-muted-foreground">{t('filters.airports')}</span>
        {airports.isLoading ? (
          <span className="flex items-center gap-1 text-xs text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> {t('common:common.loading')}
          </span>
        ) : airports.data && airports.data.length > 0 ? (
          <div className="flex flex-wrap gap-1" role="group" aria-label={t('common:topbar.airportScope')}>
            {airports.data.map((a) => {
              const active = selectedAirports.has(a.code);
              return (
                <button
                  key={a.code}
                  type="button"
                  aria-pressed={active}
                  onClick={() => toggleAirport(a.code)}
                  className={`rounded border px-1.5 py-0.5 text-[11px] transition-colors ${
                    active
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border bg-background text-foreground hover:bg-muted'
                  }`}
                  title={a.label}
                >
                  {a.code}
                </button>
              );
            })}
          </div>
        ) : (
          <span className="text-xs text-muted-foreground">{t('filters.noAirports')}</span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Filter label={t('filters.direction.label')}>
          <div className="flex gap-1">
            {DIRECTIONS.map((d) => (
              <button
                key={d.value}
                type="button"
                aria-pressed={direction === d.value}
                onClick={() => update('direction', d.value === 'any' ? '' : d.value)}
                className={`rounded border px-1.5 py-0.5 text-[11px] transition-colors ${
                  direction === d.value
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border bg-background'
                }`}
              >
                {t(d.labelKey)}
              </button>
            ))}
          </div>
        </Filter>

        <Filter label={t('filters.status')}>
          <select
            value={params.get('status') ?? ''}
            onChange={(e) => update('status', e.target.value)}
            className="rounded border border-input bg-background px-1.5 py-0.5 text-xs"
          >
            {STATUS_OPTIONS.map((s) => (
              // `value` stays the raw wire status (it rides `?status=` to the
              // BFF); only the CAPTION is translated — English keeps the enum
              // token, Spanish reads the same words the board's STATUS cell shows.
              <option key={s} value={s}>
                {s ? t(`filters.statusOption.${s}`) : t('filters.anyStatus')}
              </option>
            ))}
          </select>
        </Filter>

        {/* Only flights under an open disruption — server-side over the whole
            board (`?disrupted=1`), so it finds them beyond the loaded pages. */}
        <button
          type="button"
          aria-pressed={params.get('disrupted') === '1'}
          onClick={() => update('disrupted', params.get('disrupted') === '1' ? '' : '1')}
          data-testid="filter-disrupted"
          className={`rounded border px-1.5 py-0.5 text-[11px] transition-colors ${
            params.get('disrupted') === '1'
              ? 'border-primary bg-primary/10 text-primary'
              : 'border-border bg-background text-foreground hover:bg-muted'
          }`}
          title={t('filters.inDisruptionHint')}
        >
          {t('filters.inDisruption')}
        </button>

        <Filter label={t('filters.search')}>
          <input
            type="text"
            placeholder={t('filters.searchPlaceholder')}
            value={params.get('q') ?? ''}
            onChange={(e) => update('q', e.target.value)}
            className="w-36 rounded border border-input bg-background px-1.5 py-0.5 text-xs"
          />
        </Filter>

        <Filter label={t('filters.from')}>
          <input
            type="date"
            value={params.get('from') ?? ''}
            onChange={(e) => update('from', e.target.value)}
            className="rounded border border-input bg-background px-1.5 py-0.5 text-xs"
          />
        </Filter>

        <Filter label={t('filters.to')}>
          <input
            type="date"
            value={params.get('to') ?? ''}
            onChange={(e) => update('to', e.target.value)}
            className="rounded border border-input bg-background px-1.5 py-0.5 text-xs"
          />
        </Filter>

        <Button
          variant="ghost"
          size="sm"
          className="ml-auto"
          onClick={() => setParams(new URLSearchParams(), { replace: true })}
        >
          {t('filters.clear')}
        </Button>
      </div>
    </div>
  );
}

function Filter({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex items-center gap-2">
      <span className="text-xs uppercase text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}
